import type { Json, SiteConfig } from "../types";

const THRESHOLDS = [25, 50, 75, 90] as const;

export interface ScrollTracker {
  destroy(): void;
  getMaxScrollDepth(): number;
  reset(): void;
}

export interface ScrollTrackerOptions {
  emit: (eventName: string, props: Record<string, Json>) => void;
  getConfig: () => SiteConfig | null;
}

export function installScrollTracker(opts: ScrollTrackerOptions): ScrollTracker {
  let maxDepth = 0;
  let fired = new Set<number>();
  let ticking = false;

  function shouldTrack(): boolean {
    const c = opts.getConfig();
    return !(c && c.tracking && c.tracking.scroll === false);
  }

  function compute(): number {
    const docEl = document.documentElement;
    const body = document.body;
    const scrollTop = window.scrollY || docEl.scrollTop || body.scrollTop || 0;
    const viewport = window.innerHeight || docEl.clientHeight || 0;
    const total = Math.max(
      body.scrollHeight,
      docEl.scrollHeight,
      body.offsetHeight,
      docEl.offsetHeight,
      docEl.clientHeight,
    );
    if (total <= viewport) return 100;
    return Math.min(100, Math.round(((scrollTop + viewport) / total) * 100));
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      if (!shouldTrack()) return;
      const depth = compute();
      if (depth > maxDepth) maxDepth = depth;
      for (const t of THRESHOLDS) {
        if (!fired.has(t) && depth >= t) {
          fired.add(t);
          opts.emit("scroll_depth", { threshold: t });
        }
      }
    });
  }

  window.addEventListener("scroll", onScroll, { passive: true });

  return {
    destroy(): void {
      window.removeEventListener("scroll", onScroll);
    },
    getMaxScrollDepth(): number {
      return maxDepth;
    },
    reset(): void {
      maxDepth = 0;
      fired = new Set();
    },
  };
}
